<script setup lang="ts">
// Métadonnées de la page planificateur de menus
import { LazyMealPlannerModal } from '#components'
import dayjs from 'dayjs'
import locale_fr from 'dayjs/locale/fr'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import draggable from 'vuedraggable'

useHead({
  title: 'Planificateur de menus - Glouton',
  meta: [
    { name: 'description', content: 'Planifiez vos menus de la semaine avec notre planificateur intelligent. Organisez vos repas et économisez du temps.' },
    { property: 'og:title', content: 'Planificateur de menus - Glouton' },
    { property: 'og:description', content: 'Planifiez vos menus de la semaine avec notre planificateur intelligent. Organisez vos repas et économisez du temps.' },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: '/meal-planner',
    fr: '/planificateur',
  },
})

definePageMeta({
  name: 'MealPlannerPage',
})

const userStore = useUserStore()
const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const overlay = useOverlayWithAd()
const localePath = useLocalePath()
const eventBus = useEventBusStore()
const toast = useToast()

// your explicit refs
const showHelp = ref(false)

const insertDate = ref(null)
const days = ref([])
const isLoading = ref(false)
const start = ref(dayjs())
const end = ref(start.value.add(14, 'day'))
const formatted_start = ref('')
const formatted_end = ref('')

// Dayjs setup
dayjs.locale(locale_fr)
dayjs.extend(localizedFormat)

const tabs = computed(() => {
  return [
    {
      label: 'Liste d\'épicerie',
      to: localePath({ name: 'ShoppingListPage' }),
      active: route.name === 'ShoppingListPage___fr',
      extra: false,
    },
    {
      label: 'Planificateur',
      to: localePath({ name: 'MealPlannerPage' }),
      active: route.name === 'MealPlannerPage___fr',
      extra: true,
    },
    {
      label: 'Boîte de recettes',
      to: localePath({ name: 'RecipeBoxPage' }),
      active: route.name === 'RecipeBoxPage___fr',
      extra: false,
    },
  ]
})

const dropdownItems = {
  recipe: element => [
    {
      label: 'Voir la fiche recette',
      icon: 'i-mdi-silverware-fork-knife',
      onSelect: () => navigateTo(localePath({ name: 'RecipePage', params: { id: element.recipe_id, slug: element.slug } })),
    },
    {
      label: 'Supprimer',
      icon: 'i-mdi-delete',
      onSelect: () => deleteItem(element.id),
    },
  ],
  external: element => [
    {
      label: 'Ouvrir la recette externe',
      icon: 'i-mdi-open-in-new',
      to: element.link_url,
      target: '_blank',
    },
    {
      label: 'Supprimer',
      icon: 'i-mdi-delete',
      onSelect: () => deleteItem(element.id),
    },
  ],
  note: element => [
    {
      label: 'Modifier',
      icon: 'i-mdi-pencil',
      onSelect: () => editItem(element),
    },
    {
      label: 'Supprimer',
      icon: 'i-mdi-delete',
      onSelect: () => deleteItem(element.id),
    },
  ],
}

// Fetch calendar data
async function fetchCalendar() {
  console.log('in fetch')
  try {
    isLoading.value = true
    const res = await $fetch(`${config.public.apiUrl}/meal-planner/fetch-calendar`, {
      params: {
        start: start.value.format('YYYY-MM-DD'),
        end: end.value.format('YYYY-MM-DD'),
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    days.value = res.days.map(day => ({ ...day, f_date: dayjs(day.date) }))
    formatted_start.value = res.formatted_start
    formatted_end.value = res.formatted_end
  }
  catch (e) {
    console.error(e)
  }
  finally {
    isLoading.value = false
  }
}

// Period navigators
function previousPeriod() {
  start.value = start.value.subtract(14, 'day')
  end.value = end.value.subtract(14, 'day')
  fetchCalendar()
}
function nextPeriod() {
  start.value = start.value.add(14, 'day')
  end.value = end.value.add(14, 'day')
  fetchCalendar()
}

// CRUD actions

async function addNote(note) {
  try {
    const res = await $fetch(`${config.public.apiUrl}/meal-planner/add-note`, { method: 'POST', body: {
      date: insertDate.value,
      note,
    }, headers: {
      Authorization: `Bearer ${userStore.access_token}`,
    } })
    if (res.success === 1) {
      window.alert('note ajoutée')
      fetchCalendar()
    }
  }
  catch (e) { console.error(e) }
}
async function saveNote(element) {
  try {
    const res = await $fetch(`${config.public.apiUrl}/meal-planner/save-note`, {
      method: 'POST',
      body: { note: element },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })
    if (res.success === 1) {
      toast.add({
        title: 'Note sauvegardée',
        color: 'success',
        icon: 'i-lucide-check-circle',
      })
      element.openEditor = false
    }
  }
  catch (e) { console.error(e) }
}
async function deleteItem(id) {
  const confirmed = window.confirm('Voulez-vous vraiment supprimer cet élément ?')
  if (!confirmed)
    return

  try {
    const res = await $fetch(`${config.public.apiUrl}/meal-planner/delete-item`, { method: 'POST', body: { id }, headers: {
      Authorization: `Bearer ${userStore.access_token}`,
    } })
    if (res.success === 1) {
      fetchCalendar()
    }
  }
  catch (e) { console.error(e) }
}
function editItem(element) {
  element.openEditor = true
  element.tempValue = element.note
}
async function syncLists(evt) {
  try {
    await $fetch(`${config.public.apiUrl}/meal-planner/sync-lists`, { method: 'POST', body: { days: days.value }, headers: {
      Authorization: `Bearer ${userStore.access_token}`,
    } })
  }
  catch (e) { console.error(e) }
}
async function openMealPlannerModal(date) {
  const modal = overlay.create(LazyMealPlannerModal, {
    props: { date },
  })
  modal.open()
}
watch(
  () => eventBus.lastEvent,
  (event) => {
    if (event === 'meal-planner:updated') {
      fetchCalendar()
    }
  },
)
onMounted(() => {
  if (userStore.user && userStore.user.extra == 1)
    fetchCalendar()
})

// Watcher pour recharger le calendrier quand le statut utilisateur change
watch(() => userStore.user, (newUser, oldUser) => {
  // Si l'utilisateur passe de null/false à un objet utilisateur ET qu'il est extra
  if (newUser && !oldUser && newUser.extra == 1) {
    fetchCalendar()
  }
}, { immediate: false })
</script>

<template>
  <div id="MealPlannerPage">
    <TopNav
      title="Planificateur de menus"
      description="Un calendrier pour planifier vos repas, peu importe la source de vos recettes."
      :tabs="tabs"
      class="max-w-8xl mx-auto"
      access="Un abonnement Extra est requis pour utiliser cet outil."
      :show-help-button="userStore.user && userStore.user.extra == 1"
      @help-button-click="showHelp = !showHelp"
    />
    <UContainer id="MealPlannerPage-anchor">
      <div v-if="userStore.user && userStore.user.extra == 1 && !showHelp" class="space-y-6 mt-6">
        <div class="flex justify-between">
          <div v-if="formatted_start" class="flex items-center px-3 py-2 bg-white rounded-lg">
            <UIcon v-if="isLoading" name="i-mdi-loading" class="mr-2 animate-spin" />
            <UIcon v-else name="i-mdi-calendar" class="mr-2" />
            <div class="text-sm leading-tight">
              <span class="block sm:inline sm:mr-1">{{ formatted_start }} <b>au</b></span>
              <span>{{ formatted_end }}</span>
            </div>
          </div>
          <div class="flex items-center">
            <UFieldGroup class="divide-x-2">
              <UButton color="secondary" icon="i-mdi-chevron-left" class="" @click="previousPeriod" />
              <UButton color="secondary" icon="i-mdi-chevron-right" class="" @click="nextPeriod" />
            </UFieldGroup>
          </div>
        </div>

        <div class="">
          <ul class="grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7">
            <li v-for="day in days" :key="day.date" class="col-span-1 text-center divide-y rounded-b-md border-poivre-200 divide-poivre-200" :class="day.today ? 'border-2 border-tomate-200' : 'border'">
              <div :class="day.weekend ? 'bg-saumon' : 'bg-sorbet'" class="flex items-center justify-between px-2 py-2">
                <div class="leading-none text-left">
                  <span class="text-sm font-semibold">{{ day.f_date.format('dddd') }}</span><br>
                  <span class="font-normal">{{ day.f_date.format('LL') }}</span>
                </div>

                <UButton size="sm" icon="i-mdi-plus" @click="openMealPlannerModal(day.date)" />
              </div>
              <div class="h-full">
                <draggable :list="day.items" group="people" item-key="id" class="h-full select-none min-h-30" animation="150" handle=".handle" @end="syncLists">
                  <template #item="{ element }">
                    <div
                      v-if="element.type === 'recipe'"
                      class="flex flex-col m-2 text-center border-poivre-300 border-solid rounded-lg shadow hover:shadow-md bg-tomate"
                    >
                      <div class="relative aspect-w-36 aspect-h-25">
                        <img
                          class="flex-shrink-0 w-full mx-auto bg-black rounded-t-lg"
                          :src="`${$config.public.backendCdnUrl}${element.thumb}`"
                          alt=""
                        >

                        <div
                          class="absolute flex items-center justify-center w-6 h-6 ml-2 bg-tomate-400 rounded-full left-0 top-2 opacity-60 hover:opacity-90 handle z-5"
                        >
                          <UIcon name="i-mdi-arrow-all" class="w-5 h-5 text-white cursor-move" aria-hidden="true" />
                        </div>

                        <UDropdownMenu :items="[dropdownItems.recipe(element)]" class="absolute right-2 top-2 opacity-80" :content="{ align: 'end', sideOffset: 1 }">
                          <UButton icon="i-mdi-menu-down" size="xs" color="primary" variant="solid" />
                        </UDropdownMenu>
                      </div>
                      <div class="p-2 text-sm/4 text-white">
                        {{ element.title }}
                      </div>
                    </div>

                    <div
                      v-else-if="element.type === 'external'"
                      class="flex flex-col m-2 text-center border-poivre-300 border-solid rounded-lg shadow hover:shadow-md bg-sauge-500"
                    >
                      <div class="relative aspect-w-36 aspect-h-25">
                        <img
                          class="flex-shrink-0 w-full mx-auto bg-black rounded-t-lg"
                          :src="`${$config.public.backendCdnUrl}${element.thumb}`"
                          alt=""
                        >
                        <div
                          class="absolute flex items-center justify-center w-6 h-6 ml-2 bg-sauge-400 rounded-full left-0 top-2 opacity-60 hover:opacity-90 handle z-5"
                        >
                          <UIcon name="i-mdi-arrow-all" class="w-5 h-5 text-white cursor-move" aria-hidden="true" />
                        </div>

                        <UDropdownMenu :items="[dropdownItems.external(element)]" class="absolute right-2 top-2 opacity-80" :content="{ align: 'end', sideOffset: 1 }">
                          <UButton icon="i-mdi-menu-down" size="xs" color="tertiary" variant="solid" />
                        </UDropdownMenu>
                      </div>
                      <div class="p-2 text-sm/4 text-white">
                        {{ element.title }}
                      </div>
                    </div>

                    <div
                      v-else-if="element.type === 'note'"
                      class="relative flex flex-col p-2 m-2 text-sm text-center break-all border-poivre-300 border-solid rounded-lg shadow hover:shadow-md bg-bleuet"
                    >
                      <div v-if="element.openEditor" class="">
                        <UTextarea
                          v-model="element.note"
                          color="secondary"
                          variant="subtle"
                          class="w-full"
                        />
                        <div class="grid sm:grid-cols-2 gap-3 mt-2">
                          <UButton icon="i-mdi-close" color="secondary" class="justify-center" @click="element.openEditor = false;element.note = element.tempValue" />
                          <UButton icon="i-mdi-content-save" color="secondary" class="justify-center" @click="saveNote(element)" />
                        </div>
                      </div>
                      <div v-else>
                        <div
                          class="absolute flex items-center justify-center w-6 h-6 ml-2 rounded-full left-0 top-2 opacity-60 hover:opacity-90 handle"
                          style="background-color:#5f95c2"
                        >
                          <UIcon name="i-mdi-arrow-all" class="w-5 h-5 text-white cursor-move" aria-hidden="true" />
                        </div>

                        <UDropdownMenu :items="[dropdownItems.note(element)]" class="absolute right-2 top-2 opacity-80" :content="{ align: 'end', sideOffset: 1 }">
                          <UButton icon="i-mdi-menu-down" size="xs" color="secondary" variant="solid" />
                        </UDropdownMenu>
                        <div class="text-sm/4 mt-8 text-white" v-html="element.note" />
                      </div>
                    </div>
                  </template>
                </draggable>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div v-else class="py-6">
        <MealPlannerHelp />
      </div>
    </UContainer>
  </div>
</template>
